import { Check, X } from "lucide-react";
import type { Folder } from "@onemileauto/shared-types";
import { useFolders } from "../../hooks/use-folders";
import { iconForFolderSlug } from "../../lib/folder-icons";

interface MoveToFolderSheetProps {
  mailboxId: string;
  currentFolderId: string;
  onMove: (folder: Folder) => void;
  onClose: () => void;
  /** Disables every row while a move is in flight, so a double-tap can't fire two moves. */
  moving?: boolean;
}

/**
 * Generalizes the single "Archive" shortcut into "move this thread to any
 * folder" (Phase 6) — one folder per thread, so picking a row here is a
 * full replace, not an add (see the architecture plan's locked folder
 * model). The fast Archive button in thread-detail.tsx's header stays as
 * its own one-tap affordance; this sheet is the "anywhere else" path,
 * opened from that same header (see the FolderInput button next to
 * Archive) — a thread-level action, not per-message, since a thread only
 * ever lives in one folder regardless of which message in it you're
 * looking at.
 */
export function MoveToFolderSheet({ mailboxId, currentFolderId, onMove, onClose, moving }: MoveToFolderSheetProps) {
  const { data } = useFolders(mailboxId);
  const folders = data?.folders ?? [];

  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-cream md:items-center md:justify-center md:bg-charcoal/40 md:p-6">
      <div className="flex min-h-0 w-full flex-1 flex-col bg-cream md:h-auto md:max-h-[80vh] md:max-w-sm md:flex-none md:rounded-xl md:border md:border-border md:shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="font-display text-lg text-charcoal">Move to folder</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-10 w-10 items-center justify-center rounded-full text-muted hover:bg-parchment-dim hover:text-charcoal"
          >
            <X size={20} />
          </button>
        </div>

        <ul className="flex flex-1 flex-col gap-0.5 overflow-y-auto p-2 md:flex-none">
          {folders.map((folder) => {
            const isCurrent = folder.id === currentFolderId;
            const Icon = iconForFolderSlug(folder.slug);
            return (
              <li key={folder.id}>
                <button
                  type="button"
                  onClick={() => !isCurrent && onMove(folder)}
                  disabled={isCurrent || moving}
                  aria-current={isCurrent ? "true" : undefined}
                  className={`flex h-12 w-full items-center gap-3 rounded-md px-3 text-left text-[15px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rust focus-visible:ring-offset-2 focus-visible:ring-offset-cream ${
                    isCurrent ? "text-muted" : "text-charcoal hover:bg-parchment-dim disabled:opacity-50"
                  }`}
                >
                  <Icon size={19} strokeWidth={1.75} className="shrink-0 text-muted" aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{folder.name}</span>
                  {isCurrent && <Check size={18} className="shrink-0 text-rust-deep" aria-label="Current folder" />}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
